// src/pages/RegisterPage.js

import React, { useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

// GOOGLE SIGNIN (ADDED)
import { GoogleLogin } from '@react-oauth/google'

function RegisterPage() {
  const navigate = useNavigate()

  // 1) Determine current language from localStorage (default 'en')
  const storedLang = localStorage.getItem('lang') || 'en'
  const [selectedLang, setSelectedLang] = useState(storedLang)

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    preferredLanguage: 'en',
  })

  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // 2) Basic translations
  const translations = {
    en: {
      pageTitle: 'Create Your PayMe Account',
      pageSubtitle: 'Register with us and start sending or receiving money!',
      fullName: 'Full Name',
      namePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      password: 'Password',
      passwordPlaceholder: 'Enter password',
      registerButton: 'Register',
      alreadyHave: 'Already have an account?',
      loginLink: 'Login',
      registrationFailed: 'Registration failed',
      pleaseLogin: 'Please log in first.',
      processing: 'Processing...',
      selectLanguage: 'Language',
      passwordRequirements:
        'Password must be at least 8 characters, include an uppercase letter, and a special character.',
      invalidPasswordError:
        'Password must be at least 8 characters, include an uppercase letter, and a special character.',
      preferredLanguageLabel: 'Preferred Language',
      googleSignInBtn: 'Sign in with Google', // GOOGLE SIGNIN (ADDED)
    },
    ja: {
      pageTitle: 'PayMeアカウントを作成',
      pageSubtitle: '登録して送金や支払い依頼を始めましょう！',
      fullName: '氏名',
      namePlaceholder: 'お名前を入力',
      email: 'メールアドレス',
      emailPlaceholder: 'メールアドレスを入力',
      password: 'パスワード',
      passwordPlaceholder: 'パスワードを入力',
      registerButton: '登録',
      alreadyHave: 'すでにアカウントをお持ちですか？',
      loginLink: 'ログイン',
      registrationFailed: '登録に失敗しました',
      pleaseLogin: '最初にログインしてください。',
      processing: '処理中...',
      selectLanguage: '言語',
      passwordRequirements:
        'パスワードは8文字以上で、大文字と特殊文字を含める必要があります。',
      invalidPasswordError:
        'パスワードは8文字以上で、大文字と特殊文字を含める必要があります。',
      preferredLanguageLabel: '希望言語',
      googleSignInBtn: 'Googleで登録', // GOOGLE SIGNIN (ADDED)
    },
    pt: {
      pageTitle: 'Crie sua conta PayMe',
      pageSubtitle: 'Registre-se e comece a enviar ou receber dinheiro!',
      fullName: 'Nome Completo',
      namePlaceholder: 'Digite seu nome',
      email: 'E-mail',
      emailPlaceholder: 'Digite seu e-mail',
      password: 'Senha',
      passwordPlaceholder: 'Digite sua senha',
      registerButton: 'Registrar',
      alreadyHave: 'Já tem uma conta?',
      loginLink: 'Login',
      registrationFailed: 'Falha no registro',
      pleaseLogin: 'Por favor, faça login primeiro.',
      processing: 'Processando...',
      selectLanguage: 'Idioma',
      passwordRequirements:
        'A senha deve ter pelo menos 8 caracteres, incluir uma letra maiúscula e um caractere especial.',
      invalidPasswordError:
        'A senha deve ter pelo menos 8 caracteres, incluir uma letra maiúscula e um caractere especial.',
      preferredLanguageLabel: 'Idioma Preferido',
      googleSignInBtn: 'Registrar com Google', // GOOGLE SIGNIN (ADDED)
    },
  }

  const t = translations[selectedLang] || translations.en

  const handleLanguageChange = (e) => {
    const newLang = e.target.value
    setSelectedLang(newLang)
    localStorage.setItem('lang', newLang)
    window.location.reload()
  }

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*[\W_]).{8,}$/
    return regex.test(password)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    // 1) Validate password client-side
    if (!validatePassword(form.password)) {
      setError(t.invalidPasswordError)
      setIsSubmitting(false)
      return
    }

    try {
      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/register',
        {
          name: form.name,
          email: form.email,
          password: form.password,
          preferredLanguage: form.preferredLanguage,
        }
      )

      // On success, store token & user
      const { token, user } = res.data
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))

      // Go to the dashboard
      navigate('/dashboard')
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.message || t.registrationFailed)
      setIsSubmitting(false)
    }
  }

  // GOOGLE SIGNIN (ADDED)
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const { credential } = credentialResponse
      if (!credential) {
        setError('Google authentication failed')
        return
      }

      // Call our backend
      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/google-login',
        { credential }
      )

      const { token, user } = res.data
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))

      // Go to dashboard
      navigate('/dashboard')
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.message || 'Google login failed')
    }
  }

  // GOOGLE SIGNIN (ADDED)
  const handleGoogleError = () => {
    setError('Google Sign-In was cancelled or failed')
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-gray-50 p-4 relative">
      {/* Language selector in the top-right corner */}
      <div className="absolute top-4 right-4">
        <label htmlFor="lang-select" className="mr-2 text-sm font-medium">
          {t.selectLanguage}:
        </label>
        <select
          id="lang-select"
          value={selectedLang}
          onChange={handleLanguageChange}
          className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
        >
          <option value="en">English</option>
          <option value="ja">日本語</option>
          <option value="pt">Português</option>
        </select>
      </div>

      {/* Heading and optional brand/logo */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-700">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-gray-500">{t.pageSubtitle}</p>
      </div>

      {/* Card Container */}
      <div className="w-full max-w-md bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label
              htmlFor="name"
              className="block mb-1 text-gray-700 font-medium"
            >
              {t.fullName}
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={t.namePlaceholder}
            />
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block mb-1 text-gray-700 font-medium"
            >
              {t.email}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={t.emailPlaceholder}
            />
          </div>

          {/* Password Requirements Note */}
          <div className="text-xs text-gray-600 mb-1">
            {t.passwordRequirements}
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block mb-1 text-gray-700 font-medium"
            >
              {t.password}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck="false"
              style={{ WebkitTextSecurity: 'disc' }}
              value={form.password}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={t.passwordPlaceholder}
            />
          </div>

          {/* Preferred Language dropdown */}
          <div>
            <label className="block mb-1 text-gray-700 font-medium">
              {t.preferredLanguageLabel}
            </label>
            <select
              name="preferredLanguage"
              value={form.preferredLanguage}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="en">English</option>
              <option value="ja">日本語</option>
              <option value="pt">Português</option>
            </select>
          </div>

          {/* Error message if any */}
          {error && (
            <p className="text-red-600 text-sm font-medium">{error}</p>
          )}

          {/* Submit button => normal vs "Processing..." */}
          <div>
            {isSubmitting ? (
              <button
                type="button"
                disabled
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors 
                           flex items-center justify-center"
              >
                <svg
                  aria-hidden="true"
                  role="status"
                  className="inline w-4 h-4 mr-2 text-white animate-spin"
                  viewBox="0 0 100 101"
                  fill="none"
                >
                  <path
                    d="M100 50.5908C100 78.2055 77.6142 
                       100.591 50 100.591C22.3858 100.591 
                       0 78.2055 0 50.5908C0 22.9762 
                       22.3858 0.59082 50 0.59082C77.6142 
                       0.59082 100 22.9762 100 50.5908ZM9.08197 
                       50.5908C9.08197 73.1865 27.4043 91.5089 
                       50 91.5089C72.5957 91.5089 90.918 
                       73.1865 90.918 50.5908C90.918 27.9951 
                       72.5957 9.67272 50 9.67272C27.4043 
                       9.67272 9.08197 27.9951 9.08197 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 
                       38.4038 97.8624 35.9116 97.0211 
                       33.5534C95.3428 28.8221 92.871 
                       24.3692 89.8167 20.4415C85.8439 
                       15.1192 80.8826 10.7237 74.8586 
                       7.6974C68.8345 4.6711 61.9876 
                       3.12324 55.0019 3.12317L54.6331 
                       3.12926C49.8112 3.22189 45.367 
                       7.0027 44.7502 11.8138C44.0899 
                       16.9254 48.0476 21.3346 53.1592 
                       21.9949C57.6566 22.5846 61.9175 
                       24.3462 65.4215 27.1059C68.9254 
                       29.8656 71.6086 33.5431 73.1016 
                       37.7886C74.0713 40.5842 76.7224 
                       42.5852 79.75 42.5455C82.7686 
                       42.5059 85.3234 40.5075 86.3536 
                       37.733C87.6151 34.2106 90.1405 
                       31.2339 93.9676 29.3501V39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                {t.processing}
              </button>
            ) : (
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors"
              >
                {t.registerButton}
              </button>
            )}
          </div>
        </form>

        {/* Center the Google button */}
        <div className="mt-4 text-center">
          <p className="text-gray-500 text-sm mb-2">OR</p>
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text="continue_with"
            />
          </div>
        </div>

        <div className="text-center mt-4">
          <p className="text-sm text-gray-500">
            {t.alreadyHave}{' '}
            <span
              onClick={() => {
                window.location.href = 'https://you.payme.co.jp/'
              }}
              className="text-blue-600 font-medium cursor-pointer hover:underline"
            >
              {t.loginLink}
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
